import { FormEvent, useState } from 'react'
import { faSparkles } from '@fortawesome/pro-duotone-svg-icons'

import Button from '../../../components/button/Button'
import Icon from '../../../components/icon/Icon'
import { PropertyType } from '../../../_types/property'
import { OnboardingStepProps } from '../types'
import { StepBody, StepFooter, ErrorBanner, Spacer } from '../styled'
import { PROPERTY_TYPE_OPTIONS } from '../helpers'
import {
  TilesGrid,
  Tile,
  TileEmoji,
  TileTitle,
  TileDescription,
} from '../../../components/choice-tile/styled'

/**
 * Step 4: property types — multi-select using the same tile visual as
 * dwelling-scope but allowing multiple selections.
 *
 * @param {OnboardingStepProps} props
 * @returns {JSX.Element}
 */
const PropertyTypesStep = ({
  profile,
  onSubmit,
  onBack,
  isSubmitting,
}: OnboardingStepProps): JSX.Element => {
  const initialPreferences = profile?.unitPreferences

  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>(
    initialPreferences?.propertyTypes ?? [],
  )
  const [errorMessage, setErrorMessage] = useState<string>('')

  const togglePropertyType = (value: PropertyType): void => {
    if (propertyTypes.includes(value)) {
      setPropertyTypes(propertyTypes.filter(existing => existing !== value))
      return
    }

    setPropertyTypes([...propertyTypes, value])
  }

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault()
    setErrorMessage('')

    if (propertyTypes.length === 0) {
      setErrorMessage("Pick at least one — we'll surface listings for it.")
      return
    }

    await onSubmit({
      unitPreferences: {
        priceMin: initialPreferences?.priceMin ?? null,
        priceMax: initialPreferences?.priceMax ?? null,
        bedroomCounts: initialPreferences?.bedroomCounts ?? [],
        bathroomCounts: initialPreferences?.bathroomCounts ?? [],
        cities: initialPreferences?.cities ?? [],
        dwellingScope: initialPreferences?.dwellingScope ?? 'no-preference',
        preferredMoveInDate: initialPreferences?.preferredMoveInDate ?? null,
        propertyTypes,
      },
      onboardingCompletedAt: Date.now(),
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <StepBody>
        <TilesGrid>
          {PROPERTY_TYPE_OPTIONS.map(option => {
            const isSelected = propertyTypes.includes(option.value)
            return (
              <Tile
                key={option.value}
                type="button"
                isSelected={isSelected}
                onClick={() => togglePropertyType(option.value)}
              >
                <TileEmoji aria-hidden="true">
                  <Icon icon={option.icon} />
                </TileEmoji>
                <TileTitle>{option.title}</TileTitle>
                <TileDescription>{option.description}</TileDescription>
              </Tile>
            )
          })}
        </TilesGrid>
        {errorMessage !== '' ? <ErrorBanner>{errorMessage}</ErrorBanner> : null}
      </StepBody>
      <StepFooter>
        {onBack === null ? (
          <Spacer />
        ) : (
          <Button type="button" variant="ghost" onClick={onBack}>
            ← Back
          </Button>
        )}
        <Button type="submit" isLoading={isSubmitting}>
          Finish setup <Icon icon={faSparkles} />
        </Button>
      </StepFooter>
    </form>
  )
}

export default PropertyTypesStep
